import { defineStore } from 'pinia'

import * as paymentRequestApi from '../services/api/payment-request'
import { IApproval } from './staff'

export interface IInvoiceData {
  note: string
  amount?: number
  unit?: string
  _amount?: string
}

export interface IReceiver {
  bank_info: string
  phone: string
  bank_name: string
  bank_no: string
}

export interface IPaymentRequestData {
  id: number
  status: string
  type: string
  is_urgent: boolean
  note: string
  reason: string
  approvals: Array<IApproval>
  invoices: Array<IInvoiceData>
  method: string
  receiver: IReceiver
  images: Array<any>
  bill_images: Array<any>
  assignee_id: number
  car_id: number
  customer_id: number
  assignee: any
  car: any
  customer: any
  created_at: string
}

export const usePaymentRequestStore = defineStore('paymentRequest', {
  state: () => ({
    paymentRequest: {
      id: 0,
      status: '',
      type: '',
      is_urgent: false,
      note: '',
      reason: '',
      approvals: [],
      invoices: [],
      method: '',
      receiver: {
        bank_info: '',
        phone: '',
        bank_name: '',
        bank_no: '',
      },
      images: [],
      bill_images: [],
      assignee_id: 0,
      car_id: 0,
      customer_id: 0,
      assignee: {},
      car: {},
      customer: {},
      created_at: '',
    } as IPaymentRequestData,
    isLoading: false,
  }),

  actions: {
    setPaymentRequest(data: IPaymentRequestData) {
      this.paymentRequest = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getPaymentRequest(id: number) {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.get(id)
        this.setPaymentRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updatePaymentRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.update(this.paymentRequest.id, payload)
        this.setPaymentRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyPaymentRequest() {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.apply(this.paymentRequest.id)
        this.setPaymentRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectPaymentRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.reject(this.paymentRequest.id, payload)
        this.setPaymentRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakePaymentRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.remake(this.paymentRequest.id, payload)
        this.setPaymentRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approvePaymentRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.approve(this.paymentRequest.id, payload)
        this.setPaymentRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async payPaymentRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.pay(this.paymentRequest.id, payload)
        this.setPaymentRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const usePaymentRequestListStore = defineStore('paymentRequestList', {
  state: () => ({
    paymentRequests: [] as IPaymentRequestData[],
    count: 0,
    total: 0,
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addPaymentRequest(data: IPaymentRequestData) {
      this.paymentRequests.push(data)
    },

    setPaymentRequests(data: IPaymentRequestData[]) {
      this.paymentRequests = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countPaymentRequests() {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createPaymentRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await paymentRequestApi.create(payload)
        this.addPaymentRequest(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listPaymentRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await paymentRequestApi.list(params)
        const { data, total } = result as any
        this.setPaymentRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllPaymentRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await paymentRequestApi.listAll(params)
        const { data, total } = result as any
        this.setPaymentRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
